import { Paper, withStyles } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import DataGrid from 'devextreme-react/data-grid';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import DataSource from 'devextreme/data/data_source';
import SummaryTableSchema from './SummaryTableSchema';
import SummaryTable from 'components/common/SummaryTable';
import ImageView from 'components/common/ImageView';
import { columns, columnsNotes, imgLinks } from './ProductDetails.constants';
import styles, { SummaryPaperStyles } from './ProductDetails.styles';
import { useRetrieveAutoICatalogueProductDetails, useRetrieveAutoICatalogueProductDetailsSummary } from 'api/Worksale/AutoInfoCatalogue/autoInfoCatalogue';
import { useSPContextSelector } from 'components/Worksale/Worksale.context';
import Selectors from 'components/Worksale/Worksale.selectors';
import { useCalculateFastLineDetail, useCreateFastLine, useCurrentWorksale, useRetrieveImage } from 'api/Worksale/catalogue';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { useValidateProduct } from 'api/Worksale/worksaleLatest';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { isScreenRequest } from 'api/utils';
import { isNull } from 'utils/utils';
import { AddCircle, ArrowBack } from '@markinson/uicomponents-v2/SvgIcons/';
import AddToSale from 'components/Worksale/ProductCatalogue/AddToSale';
import { IAddToSaleHandle } from 'components/Worksale/ProductCatalogue/AddToSale/AddToSale.properties';
import { usePriceOverrideAndKitValidations } from '../../Worksale.hooks';
import PriceOverrideAuthorityDialog from 'components/common/PriceOverrideAuthorityDialog';
import BOMDialog from 'components/common/BOMDialog';
import { ICalculateFastLineCriteriaFacade, ICreateFastLineFacade } from 'api/swaggerTypes';
import { IPriceOverrideData } from 'components/common/PriceOverrideAuthorityDialog/PriceOverrideAuthorityDialog.properties';

const serviceUnavailable = 503;

const ProductDetails = ({ classes, allowPriceQuantityDialogAutoi, includeMoProImages, changeSelectedTab }, ref) => {
    const params = new URLSearchParams(location.search);
    const searchPartId = params.get('VehiclePartId');
    const searchProductId = params.get('VehicleProductId');
    const searchQuantity = params.get('searchQuantity');
    const partDescription = params.get('PartDescription');
    const PartId = searchPartId ? Number(searchPartId) : null;
    const ProductId = Number(searchProductId);
    const Quantity = searchQuantity ? Number(searchQuantity) : 1;

    const {
        multipleBomOpen,
        bomList,
        kitStatus,
        priceOverrideDialogOpen,
        priceOverrideScreenType,
        priceOverrideDetailSummary,
        selectedBomCode,
        validate,
        // resetStates: resetPriceOverrideBomStates,
        isPriceOverrideScreenRequest,
        setKitStatus,
        setSelectedBomCode,
        setMultipleBomState,
        setPriceOverrideDialogOpen,
        setPriceOverrideDetailSummary
    } = usePriceOverrideAndKitValidations();

    const WorksaleId = useSPContextSelector(Selectors.WorksaleId);
    const itemDescription = useSPContextSelector<'ItemDescription'>((state) => state.ItemDescription) || partDescription || '';

    const dataGridInnerRef = React.useRef<DataGrid>();
    const [productDataSource, setProductDataSource] = React.useState<DataSource>(new DataSource([]));
    const [productNotesDataSource, setProductNotesDataSource] = React.useState<DataSource>(new DataSource([]));
    const { setActionBar } = React.useContext(ActionBarContext);
    const validateProductMutation = useValidateProduct();
    const { data: worksaleData } = useCurrentWorksale();
    const calculateFastLineDetailMutation = useCalculateFastLineDetail();
    const createFastLineMutation = useCreateFastLine();
    const [addToSaleOpen, setAddToSaleOpen] = React.useState(false);
    const addSaleRef = React.useRef<IAddToSaleHandle>(null);
    const [imagesAdded, setImagesAdded] = useState(false);

    const autoIAPIErrorHandler = (err: any): void => {
        if (err?.res?.status === serviceUnavailable && changeSelectedTab) {
            changeSelectedTab('Lines');
        }
    };
    const { data } = useRetrieveAutoICatalogueProductDetails({ PartId, ProductId, ErrorHandler: autoIAPIErrorHandler });
    const { data: summaryData } = useRetrieveAutoICatalogueProductDetailsSummary({ ProductId, WorksaleId, ErrorHandler: autoIAPIErrorHandler });

    const fastLineInlineData = calculateFastLineDetailMutation.data?.FastLineDetails;

    const loadProducts = React.useCallback(
        async (_BatchPage, _BatchSize) => {
            const formated = data?.ProductSpecifications?.Dimensions?.Value?.map((item, index) => {
                return {
                    ...item,
                    id: index + 1,
                };
            });

            return Promise.resolve(formated);
        },
        [data]
    );

    const loadProductsNotes = React.useCallback(
        async (_BatchPage, _BatchSize) => {
            const formated = data?.ProductSpecifications?.Notes?.Value?.map((item, index) => {
                return {
                    ...item,
                    id: index + 1,
                };
            });

            return Promise.resolve(formated);
        },
        [data]
    );

    React.useEffect(
        () => {
            setActionBarButtonsForDetail();
        },
        [ProductId, summaryData]
    );

    const setActionBarButtonsForDetail = (): void => {
        setActionBar({
            leftIcons: [{
                label: 'Back',
                Icon: ArrowBack,
                action: 'Back',
            }],
            centerIcons: [],
            rightIcons: [{
                label: 'Add Line',
                Icon: AddCircle,
                action: 'AddLine',
                disabled: (ProductId === -1 || !summaryData?.inlineObject?.IsValidProduct)
            }]
        });
    };
    const retrieveImage = useRetrieveImage();
    const [images, setImages] = React.useState([]);
    const [_loading, setLoading] = React.useState(false);

    React.useEffect(
        () => {
            if (!summaryData?.inlineObject?.Images || summaryData?.inlineObject?.Images?.length === 0) return;

            if (includeMoProImages) {
                fetchImages();
            }
        },
        [summaryData?.inlineObject?.Images, imgLinks]
    );

    const notifyProductFailedToAdd = () => {
        showSnackBar({
            variant: 'error',
            message: 'Failed to add product'
        });
    };

    const notifyProductAdded = () => {
        showSnackBar({
            variant: 'success',
            message: 'Product added successfully'
        });
    };

    const calculateAndValidateFastLine = useCallback(
        (query: ICalculateFastLineCriteriaFacade) => calculateFastLineDetailMutation.mutateAsync({
            WorksaleId: worksaleData?.WorksaleId,
            ...query,
            ProductCatalogueType: 'autoi',
            BomCode: selectedBomCode,
            KitStatus: kitStatus,
        })
        .then((response) => {
            validate(null, {
                Forms: response.Forms,
                BomCode: selectedBomCode,
                KitDetail: response.FastLineDetails?.KitDetail,
                KitStatus: response.FastLineDetails?.KitStatus,
                OverrideDetails: response.FastLineDetails,
            });
            if (response?.FastLineDetails?.BomCode) {
                setSelectedBomCode(response?.FastLineDetails?.BomCode);
            }
            if (response?.FastLineDetails?.KitStatus) {
                setKitStatus(response?.FastLineDetails?.KitStatus);
            }

            return response;
        }),
        [worksaleData?.WorksaleId]
    );

    const createAndValidateFastLine = React.useCallback(
        async (ProdId: any, query: ICreateFastLineFacade) => {
          const response = await createFastLineMutation.mutateAsync({
            ...fastLineInlineData,
            ...query,
            WorksaleId: worksaleData?.WorksaleId,
            ProductId: ProdId,
            ProductCatalogueType: 'autoi',
          });

          validate(null, {
            Forms: response.Forms,
            BomCode: selectedBomCode,
            KitDetail: response.GridLineDetails?.inlineObject?.KitDetail,
            KitStatus: response.GridLineDetails?.inlineObject?.KitStatus,
            OverrideDetails: response.GridLineDetails?.inlineObject?.OverrideDetails,
          });

          if (response.Status && !response.Forms) {
            setPriceOverrideDialogOpen(false);
            notifyProductAdded();
          }

          if (!response.Status) {
            notifyProductFailedToAdd();
          }

          if (!isPriceOverrideScreenRequest(response.Forms?.[0])) {
            // productGridRef.current?.reloadLines();
          }

          return response;
        },
        [
          worksaleData?.WorksaleId,
          selectedBomCode,
          fastLineInlineData,
          notifyProductAdded,
          notifyProductFailedToAdd,
        ]
      );

    React.useImperativeHandle(
        ref,
        () => ({
            async AddLine(): Promise<void> {
                const validationResponse = await validateProductMutation.mutateAsync({
                    CustomerId: worksaleData?.Summary?.SaleHeader?.CustomerId,
                    ProductId,
                    SalesEntity: worksaleData?.Summary?.SaleHeader?.SalesEntity,
                    WarehouseEntity: worksaleData?.Summary?.SaleHeader?.WarehouseEntity
                });

                if (!validationResponse?.Status) {
                    return;
                }

                if (!Boolean(ProductId)) {
                    notifyProductFailedToAdd();

                    return;
                }

                if (allowPriceQuantityDialogAutoi) {
                    setAddToSaleOpen(true);
                } else {
                    const response = await calculateFastLineDetailMutation.mutateAsync({
                        WorksaleId: worksaleData?.WorksaleId,
                        ProductId,
                        OrderedQuantity: Quantity,
                        ProductCatalogueType: 'autoi'
                    });

                    if ((response?.FastLineDetails?.BomCode &&
                        isNull(response?.FastLineDetails?.KitStatus)) || isScreenRequest(response?.Forms)) {
                        return;
                    }

                    await createAndValidateFastLine(ProductId, {
                        ...response?.FastLineDetails,
                        OrderedQuantity: Quantity,
                    });
                }
            },
        }),
        [
            fastLineInlineData,
            worksaleData,
            ProductId,
            Quantity,
            allowPriceQuantityDialogAutoi,
            calculateFastLineDetailMutation,
            validateProductMutation,
            createAndValidateFastLine,
        ]
    );

    const fetchImages = async () => {
        setLoading(true);
        try {
            const results = await Promise.all(
                summaryData?.inlineObject?.Images.map(({ ImageObj }) =>
                    retrieveImage.mutateAsync({ ImageId: ImageObj })
                )
            );

            setImages((prev) => [...prev, ...(results.map((result) => {
                return { imageSrc: result };
            }))]);
        } catch (error) {
            console.error('Failed to fetch images:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(
        () => {
            if (data?.ProductSpecifications?.Images?.Value?.length > 0 && !imagesAdded) {
                const imagesList = data?.ProductSpecifications?.Images?.Value?.map((item) => {
                    return {
                        imageSrc: item?.ImageUrl
                    };
                });
                setImagesAdded(true);
                setImages((prev) => [...prev, ...imagesList]);
            }
        },
        [data?.ProductSpecifications?.Images, images]
    );

    React.useEffect(
        () => {
            dataGridInnerRef.current?.instance?.clearSelection();
            setProductDataSource(
                () => createDataSource('id', { fetch: loadProducts })
            );
        }
        ,
        [data]
    );

    React.useEffect(
        () => {
            dataGridInnerRef.current?.instance?.clearSelection();
            setProductNotesDataSource(
                () => createDataSource('id', { fetch: loadProductsNotes })
            );
        }
        ,
        [data]
    );

    const handleAddToLineClose = () => {
        setAddToSaleOpen(false);
    };

    const handleAddToSaleOnApply = React.useCallback(
        (query) => {
            return createAndValidateFastLine(
                ProductId,
                {
                ...query,
                BomCode: selectedBomCode,
                KitStatus: kitStatus,
            });
        },
        [createFastLineMutation.data, selectedBomCode, kitStatus, createAndValidateFastLine]
    );

    const handlePriceOverrideOk = React.useCallback(
        async (priceOverrideData: IPriceOverrideData) => {
            try {
                const response = await createAndValidateFastLine(
                createFastLineMutation.data?.GridLineDetails?.inlineObject?.ProductId,
                {
                    ...createFastLineMutation.data?.GridLineDetails.inlineObject,
                    BomCode: selectedBomCode,
                    KitStatus: kitStatus,
                    DiscountedPrice: createFastLineMutation.data?.GridLineDetails.inlineObject?.Price,
                    OverrideDetails: { ...priceOverrideDetailSummary, ...priceOverrideData },
                });
                if (response.Status && !response.Forms) {
                    setAddToSaleOpen(false);
                    setPriceOverrideDetailSummary(null);
                }
            } catch (err) {
                console.warn(err);
            }
        },
        [createFastLineMutation.data, priceOverrideDetailSummary, selectedBomCode, kitStatus, createAndValidateFastLine]
    );

    const handlePriceOverrideCancel = React.useCallback(
        () => {
            setPriceOverrideDialogOpen(false);
        },
        []
    );

    const handleActionClick = React.useCallback(
        async (status: 'c' | 'k') => {
            setMultipleBomState(false);
            setKitStatus(status);

            if (status) {
                if (status === 'c') {
                    addSaleRef.current?.disablePriceField();
                }

                calculateAndValidateFastLine({
                    ProductId: createFastLineMutation.data?.GridLineDetails?.inlineObject?.ProductId,
                    OrderedQuantity: fastLineInlineData?.OrderedQuantity,
                    KitStatus: status
                });
            } else {
                addSaleRef.current?.resetToDefault();
            }

        },
        [calculateFastLineDetailMutation.data, addSaleRef.current, selectedBomCode, createAndValidateFastLine, calculateAndValidateFastLine]
    );

    return (
        <div className={classes.parentcontainer}>
            <Scrollbars>
                <div className={classes.container}>
                    <Paper className={classes.specs}>
                        <p className={classes.specsHeading}>Specifications</p>
                        <DynamicDataGrid
                            standardKeyboardNavigation={false}
                            dataGridClassName={classes.grid}
                            columns={columns}
                            allowHeaderFilting={false}
                            allowUpdating={false}
                            allowDelete={false}
                            actionButtonsVisibility={false}
                            selection='single'
                            gridProps={{
                                dataSource: productDataSource,
                                dataGridRef: dataGridInnerRef,
                                allowColumnResizing: true,
                                keyExpr: 'Code'
                            }}
                        />
                    </Paper>

                    <Paper className={classes.image}>
                        <ImageView dialogTitle='Product Image' imageCount={images?.length} imageSrc={images?.[0]?.imageSrc} listOfImages={images} imageCaption={itemDescription} />
                    </Paper>

                    <div className={classes.summary}>
                        <SummaryTable
                            loading={false}
                            data={SummaryTableSchema}
                            ProductReceiptSummary={summaryData?.inlineObject?.CatalogueProductSummary}
                            schema={SummaryTableSchema}
                            paperStyles={{ style: SummaryPaperStyles }}
                        />
                    </div>

                    <div>
                        <Paper className={classes.specs} style={{ marginTop: -20 }}>
                            <p className={classes.specsHeading}>Technical Details</p>
                            <DynamicDataGrid
                                standardKeyboardNavigation={false}
                                dataGridClassName={classes.grid}
                                columns={columnsNotes}
                                allowHeaderFilting={false}
                                allowUpdating={false}
                                allowDelete={false}
                                actionButtonsVisibility={false}
                                selection='single'
                                gridProps={{
                                    dataSource: productNotesDataSource,
                                    dataGridRef: dataGridInnerRef,
                                    allowColumnResizing: true,
                                    keyExpr: 'Code'
                                }}
                            />
                        </Paper>
                    </div>

                    <PriceOverrideAuthorityDialog
                        open={priceOverrideDialogOpen}
                        loading={createFastLineMutation.isLoading}
                        type={priceOverrideScreenType}
                        data={priceOverrideDetailSummary}
                        onOk={handlePriceOverrideOk}
                        onCancel={handlePriceOverrideCancel}
                    />
                    <BOMDialog
                        open={multipleBomOpen}
                        onClose={() => setMultipleBomState(false)}
                        data={bomList}
                        kitStatus={kitStatus ? kitStatus.toLowerCase() : ''}
                        onActionClick={handleActionClick}
                        onSelectedRowChanged={setSelectedBomCode}
                    />

                    <AddToSale
                        innerRef={addSaleRef}
                        isAddToSaleOpen={addToSaleOpen}
                        productId={ProductId}
                        productCode={(fastLineInlineData as any)?.ProductIdDisplay || ''}
                        productName={(fastLineInlineData as any)?.ProductIdLabel}
                        productPrice={fastLineInlineData?.DiscountedPrice}
                        priceDescription={fastLineInlineData?.PriceDescription}
                        productQuantity={Quantity}
                        calculateLoading={calculateFastLineDetailMutation.isLoading}
                        handleClose={handleAddToLineClose}
                        calculateFastLine={calculateAndValidateFastLine}
                        onApply={handleAddToSaleOnApply}
                    />
                </div>
            </Scrollbars>
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.forwardRef(ProductDetails));
